<template>
    <div>
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" class="ls-schedule-view--columns">
            <LsServiceScheduleDetails
                :schedule="schedule"
                :services="allRecentAndCurrentServices"
                :property="property"
                :manual-price-quote="manualPriceQuote"
                :manual-price-quote-loading="Boolean(manualPriceQuoteLoading)"
                class="ls-schedule-view--details"
                test-id="schedule_details"
            />
            <div
                v-if="routeName === 'schedule'"
                class="ls-schedule-view--buttons"
                test-id="action_buttons"
            >
                <LsActionButton
                    v-for="action in filteredScheduleActions"
                    :key="action.action"
                    v-bind="action"
                    :service="schedule"
                />
            </div>
            <router-view v-else />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService } from 'ls-vue-common';
import gql from 'graphql-tag';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsServiceScheduleDetails from '@/components/LsServiceScheduleDetails/LsServiceScheduleDetails.vue';
import LsActionButton from '@/components/LsActionButton/LsActionButton.vue';
import { apolloClient } from '@/vue-apollo';
import Schedule from '@/graphql/queries/Schedule.gql';
import AllRecentAndCurrentServices from '@/graphql/queries/AllRecentAndCurrentServices.gql';
import DEFAULT_ALL_RECENT_AND_CURRENT_SERVICES from '@/graphql/queries/AllRecentAndCurrentServices.defaults';
import ScheduleActions from '@/graphql/queries/ScheduleActions.gql';
import ManualPriceQuotes from '@/graphql/queries/ManualPriceQuotes.gql';

const MANUAL_PRICE = 'manual_price';

const routeVariables = (route) => ({
    customer_id: _.toNumber(_.get(route, 'params.customer_id')),
    property_id: _.toNumber(_.get(route, 'params.property_id')),
    schedule_id: _.toNumber(_.get(route, 'params.schedule_id')),
});

export default {
    name: 'ScheduleView',
    components: {
        LsServiceScheduleDetails,
        LsTemplatedLoading,
        LsActionButton,
    },
    data() {
        const variables = routeVariables(this.$route);

        let allRecentAndCurrentServices = DEFAULT_ALL_RECENT_AND_CURRENT_SERVICES;
        try {
            const result = apolloClient.readQuery({
                query: AllRecentAndCurrentServices,
                variables,
            });
            if (result && result.allRecentAndCurrentServices) {
                allRecentAndCurrentServices = result.allRecentAndCurrentServices;
            }
        } catch (e) {
            // Cache miss on deep-link; the parent query populates it.
        }

        let property = {};
        try {
            property =
                apolloClient.readFragment({
                    id: `Property:${variables.property_id}`,
                    fragment: gql`
                        fragment property on Property {
                            id
                            hash_id
                            street
                            city
                            state
                            zip
                            info {
                                mowing_preferences {
                                    scope
                                }
                            }
                        }
                    `,
                }) || {};
        } catch (e) {
            // A Property cached without every selected field throws rather than returning null.
        }

        return {
            loading: 0,
            manualPriceQuoteLoading: 0,
            manualPriceQuote: null,
            loggedManualPriceOverFetch: false,
            schedule: {},
            scheduleActions: [],
            property,
            allRecentAndCurrentServices: _.concat(
                allRecentAndCurrentServices.schedules || [],
                allRecentAndCurrentServices.recommendations || [],
                allRecentAndCurrentServices.requiredServices || []
            ),
            variables,
        };
    },
    computed: {
        routeName() {
            return this.$route.name;
        },
        filteredScheduleActions() {
            return this.scheduleActions.filter((action) => action.action !== 'arrivalTimeInquiry');
        },
    },
    beforeRouteUpdate(to, from, next) {
        const variables = routeVariables(to);

        // Vue reuses this instance across sibling schedules, so `data()` does not re-run.
        // Every query watches `variables`, so reassigning it moves the view.
        if (!_.isEqual(variables, this.variables)) {
            this.variables = variables;
            this.manualPriceQuote = null;
            this.loggedManualPriceOverFetch = false;
        } else if (to.name === 'schedule') {
            // Same schedule, returning from a child route: refetch what the child mutated.
            this.$apollo.queries.scheduleActions.refetch(this.variables);
        }

        next();
    },
    apollo: {
        schedule: {
            query: Schedule,
            variables() {
                return this.variables;
            },
            result({ data, loading }) {
                if (!loading) {
                    this.schedule = {
                        // this destructuring was done because the api and frontend were built and modified simutaneously
                        // ideally we may just want to update LsServiceScheduleDetails to handle the original shape
                        ...data.schedule.schedule,
                        last_completed_schedule_event: data.schedule.last_completed_schedule_event,
                        latest_dispute_completions_count:
                            data.schedule.latest_dispute_completions_count,
                        last_scope_change: data.schedule.last_scope_change,
                        price: data.schedule.price,
                        payout: data.schedule.payout,
                        commission: data.schedule.commission,
                        fee_trust: data.schedule.fee_trust,
                        tax_amount: data.schedule.tax_amount,
                        total_amount: data.schedule.total_amount,
                        next_service_within_48_hours: data.schedule.next_service_within_48_hours,
                        next_service_is_today: data.schedule.next_service_is_today,
                        allow_pause: data.schedule.allow_pause,
                        looking_for_pro: data.schedule.looking_for_pro,
                        next_service_later_than_5_days:
                            data.schedule.next_service_later_than_5_days,
                        last_schedule_cycle_change: data.schedule.last_schedule_cycle_change,
                        pending_one_time_service_day_change:
                            data.schedule.pending_one_time_service_day_change,
                    };
                }
            },
            error(error) {
                lsErrorService.log({ error });
            },
            loadingKey: 'loading',
        },
        scheduleActions: {
            query: ScheduleActions,
            variables() {
                return this.variables;
            },
            error(error) {
                lsErrorService.log({ error });
            },
            fetchPolicy: 'cache-and-network',
            loadingKey: 'loading',
        },
        manualPriceQuote: {
            query: ManualPriceQuotes,
            client: 'lighthouse',
            variables() {
                return {
                    propertyHashId: this.property.hash_id,
                    scheduleHashId: this.schedule.hash_id,
                };
            },
            update({ manualPriceQuotes }) {
                // One event with a null `eventstatus` nulls the whole root, so destructuring
                // it would throw.
                const data = _.get(manualPriceQuotes, 'data', []);

                // A null `scheduleHashId` narrows nothing server-side, so the server answers
                // with every attempt on the property. Re-check here.
                const onThisSchedule = data.filter(
                    (quote) =>
                        _.get(quote, 'scheduleEvent.schedule.internalId') ===
                        this.variables.schedule_id
                );

                // No created-at field is selectable; the server orders newest-first. Never sort.
                return (
                    onThisSchedule.find(
                        (quote) => quote.scheduleEvent.id === this.schedule.next_scheduleevent_id
                    ) ||
                    onThisSchedule[0] ||
                    null
                );
            },
            result({ data, loading }) {
                // Latches, so the notice is one line per view, not one per cache broadcast.
                if (loading || this.loggedManualPriceOverFetch) {
                    return;
                }

                const paginatorInfo = _.get(data, 'manualPriceQuotes.paginatorInfo');
                if (_.get(paginatorInfo, 'hasMorePages')) {
                    this.loggedManualPriceOverFetch = true;
                    lsErrorService.log({
                        error: {
                            message: 'manualPriceQuotes returned more pages than the page in hand',
                            level: 'warning',
                            extra: {
                                propertyHashId: this.property.hash_id,
                                schedule_id: this.variables.schedule_id,
                                total: paginatorInfo.total,
                            },
                        },
                    });
                }
            },
            skip() {
                return (
                    _.get(this.schedule, 'service.price_type') !== MANUAL_PRICE ||
                    !this.property.hash_id
                );
            },
            error(error) {
                lsErrorService.log({ error });
            },
            fetchPolicy: 'cache-and-network',
            loadingKey: 'manualPriceQuoteLoading',
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-schedule-view--columns {
    display: flex;
    flex-direction: row;
}

.ls-schedule-view--details {
    width: 370px;
    margin-right: 20px;
    height: max-content;
}

.ls-schedule-view--buttons {
    > div {
        width: 370px;
    }

    > :nth-child(n + 2) {
        margin-top: 20px;
    }

    @media (min-width: $desktop) {
        display: flex;
        flex-wrap: wrap;
        flex: 1;
        height: max-content;
        > :nth-child(n + 2) {
            margin-top: 0;
        }
        > :nth-child(n + 3) {
            margin-top: 20px;
        }
        > :nth-child(even) {
            margin-left: 20px;
        }
    }
}
</style>
